import {DashboardWidgetDescriptor} from "./DashboardWidget";

export interface DashboardWidgetConfig {
	id: string
	visible: boolean
	span?: number
	pos?: number
}

interface DashboardLayout {
	version: number
	widgets: DashboardWidgetConfig[]
}

export class DashboardLayoutStore {
	static readonly VERSION = 1

	private readonly storageKey: string

	constructor(login: string | null) {
		this.storageKey = `sb2.dashboard.layout.${login || 'anonymous'}.v${DashboardLayoutStore.VERSION}`
	}

	load(descriptors: DashboardWidgetDescriptor[], roles: string[]): DashboardWidgetConfig[] {
		const defaults = DashboardLayoutStore.defaultConfig(descriptors, roles)

		let stored: DashboardLayout | null = null
		try {
			stored = JSON.parse(localStorage.getItem(this.storageKey) || 'null')
		} catch (e) {
			console.warn(`cannot parse dashboard layout from localStorage`, e)
		}
		if (stored == null || stored.version != DashboardLayoutStore.VERSION || !Array.isArray(stored.widgets)) {
			return defaults
		}

		const known = new Map(descriptors.map(d => [d.id, d]))
		const result: DashboardWidgetConfig[] = []
		stored.widgets.forEach(w => {
			if (w != null && known.has(w.id) && !result.some(r => r.id == w.id)) {
				const entry: DashboardWidgetConfig = {id: w.id, visible: !!w.visible}
				if (w.span == 2) entry.span = 2
				if (Number.isInteger(w.pos) && (w.pos as number) >= 0) entry.pos = w.pos
				result.push(entry)
				known.delete(w.id)
			}
		})
		defaults.forEach(d => {
			if (known.has(d.id)) result.push(d)
		})
		return result
	}

	save(config: DashboardWidgetConfig[]) {
		try {
			localStorage.setItem(this.storageKey, JSON.stringify({
				version: DashboardLayoutStore.VERSION,
				widgets: config
			} as DashboardLayout))
		} catch (e) {
			console.warn(`cannot save dashboard layout to localStorage`, e)
		}
	}

	reset() {
		try {
			localStorage.removeItem(this.storageKey)
		} catch (e) {
			console.warn(`cannot reset dashboard layout in localStorage`, e)
		}
	}

	static defaultConfig(descriptors: DashboardWidgetDescriptor[], roles: string[]): DashboardWidgetConfig[] {
		return descriptors.map(d => ({
			id: d.id,
			visible: (d.defaultRoles || []).length == 0 || roles.some(r => (d.defaultRoles || []).indexOf(r) >= 0)
		}))
	}
}
